import React, { useMemo } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend, ReferenceLine,
} from "recharts";
import { Flag, Info } from "lucide-react";
import { moneyBack } from "@/lib/botMath";
import Stat from "@/components/calculator/Stat";

const bot = (n, dec = 2) => (isFinite(n) ? `${Number(n).toLocaleString(undefined, { maximumFractionDigits: dec })} BOT` : "—");
const usd = (n) => (isFinite(n) ? `$${Math.round(n).toLocaleString()}` : "—");

/** When is the pledge back in your hands? For a fixed BOT term that is rewards
 *  PLUS the principal the term unlocks every day — much sooner than rewards alone. */
export default function BotRecoveryView({ pledge, ratePct, termDays, days, botPrice }) {
  const sim = useMemo(() => {
    const horizon = Math.max(Number(days) || 0, Math.round(Number(termDays) || 0) || 0, 30);
    const m = moneyBack({ pledge, ratePct, termDays, days: horizon });
    const rows = m.rows.map((r) => ({ ...r, pledgeLine: m.pledge }));
    return { ...m, rows, horizon };
  }, [pledge, ratePct, termDays, days]);

  const term = Math.round(Number(termDays) || 0);
  const price = Number(botPrice) || 0;
  const P = sim.pledge;
  const sooner = sim.breakEvenDay && sim.rewardsOnlyDay ? sim.rewardsOnlyDay - sim.breakEvenDay : 0;

  return (
    <div className="space-y-5" data-testid="bot-recovery-view">
      <div className="grid sm:grid-cols-3 gap-4">
        <Stat label="Pledge back in hand on" color="#00863A" testid="bot-kpi-breakeven"
          value={sim.breakEvenDay ? `Day ${sim.breakEvenDay}` : `Not within ${sim.horizon}d`}
          sub={sim.breakEvenDay
            ? `${Math.round(sim.breakEvenDay / 30.4)} month${Math.round(sim.breakEvenDay / 30.4) === 1 ? "" : "s"} — rewards taken as they land, plus the principal unlocked by then`
            : "Stretch the horizon, or pick a plan with a term"} />
        <Stat label="Rewards alone would take" color="#B45309" testid="bot-kpi-rewards-only"
          value={sim.rewardsOnlyDay ? `Day ${sim.rewardsOnlyDay}` : "—"}
          sub={term > 0
            ? (sooner > 0
              ? `The daily unlock of your own pledge gets you there ${sooner} days sooner`
              : "The daily unlock of your own pledge runs alongside the rewards")
            : "Flexible unlocks nothing, so rewards are the only way back"} />
        <Stat label={`Taken out by day ${sim.horizon}`} color="#0E7490" testid="bot-kpi-cashback"
          value={bot(sim.rows[sim.rows.length - 1]?.cashBack, 2)}
          sub={`${usd((sim.rows[sim.rows.length - 1]?.cashBack || 0) * price)} at today's BOT price · ${P ? Math.round(((sim.rows[sim.rows.length - 1]?.cashBack || 0) / P) * 100) : 0}% of the pledge`} />
      </div>

      <div className="glass p-5" data-testid="bot-recovery-chart">
        <div className="text-xs font-mono uppercase tracking-wider text-ink-3 mb-3">Getting your BOT back</div>
        <div style={{ width: "100%", height: 290 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <AreaChart data={sim.rows} margin={{ left: 8, right: 8, top: 4 }}>
              <defs>
                <linearGradient id="botCash" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00863A" stopOpacity={0.3} /><stop offset="100%" stopColor="#00863A" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,18,38,0.06)" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={(d) => `d${d}`} />
              <YAxis tick={{ fontSize: 11 }} width={58}
                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Number(v).toFixed(v < 10 ? 1 : 0))} />
              <RTooltip formatter={(v, name) => [bot(v, 4), name]} labelFormatter={(d) => `Day ${d}`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="pledgeLine" name="What you pledged" stroke="#94A3B8" strokeDasharray="5 4" fill="none" strokeWidth={1.5} />
              <Area type="monotone" dataKey="unlocked" name="Principal unlocked" stroke="#B45309" fill="none" strokeWidth={2} />
              <Area type="monotone" dataKey="rewards" name="Rewards taken" stroke="#0E7490" fill="none" strokeWidth={2} />
              <Area type="monotone" dataKey="cashBack" name="Total back in hand" stroke="#00863A" fill="url(#botCash)" strokeWidth={2.5} />
              {sim.breakEvenDay && (
                <ReferenceLine x={sim.rows.reduce((best, r) => (Math.abs(r.day - sim.breakEvenDay) < Math.abs(best - sim.breakEvenDay) ? r.day : best), 0)}
                  stroke="#00863A" strokeDasharray="4 4"
                  label={{ value: `day ${sim.breakEvenDay}`, position: "insideTopLeft", fontSize: 11, fill: "#00863A" }} />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-ink-3 mt-3 leading-relaxed" data-testid="bot-recovery-note">
          {term > 0
            ? `This assumes you take the rewards as they land instead of compounding them. A ${term}-day pledge returns ${bot(P / term, 4)} of your own pledge a day, so it is covered well before the term ends — and everything the pledge still earns after that is house money.`
            : "Flexible keeps the whole pledge available every day, so there is nothing to \"get back\" — but it also never unlocks principal on top of the rewards, so the pure-reward day is the one to watch."}
        </p>
      </div>

      <div className="glass p-4 flex items-start gap-3" data-testid="bot-recovery-flag">
        <Flag size={15} style={{ color: "#00863A" }} className="flex-shrink-0 mt-0.5" />
        <p className="text-xs text-ink-2 leading-relaxed">
          Claiming is manual on CaryPact and the wallet has to sign — nothing here moves your BOT. Any day
          you leave the rewards in instead, they compound at the plan rate, which the growth tab shows.
        </p>
      </div>

      <div className="glass p-4 flex items-start gap-3" data-testid="bot-recovery-disclaimer">
        <Info size={15} style={{ color: "#0E7490" }} className="flex-shrink-0 mt-0.5" />
        <p className="text-xs text-ink-2 leading-relaxed">
          Counted in BOT, so the break-even day doesn&apos;t move with the price. In dollars it arrives sooner
          if BOT rises and later if it falls.
        </p>
      </div>
    </div>
  );
}
